import { PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Flex, InputNumber, Modal, Radio, Space, Table, Typography } from "antd";
import { useState } from "react";
import { useErrorText } from "@/api/errors";
import { useNavigate } from "react-router";
import { api, unwrap } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { ExportButton } from "@/components/list/ExportButton";
import { DateRangeFilter, SearchFilter, StatusFilter, useSearchFilter, useStatusFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { PO_STATUSES, PoStatusTag, SupplierSelect, type PurchaseOrderRow } from "./common";

const STATUS_CHOICES = ["open", "waiting", "incoming", "draft", "received", "closed", "cancelled", "all"] as const;
const STATUS_PARAM: Record<(typeof STATUS_CHOICES)[number], string | undefined> = {
  open: "draft,submitted,approved,sent,partial",
  waiting: "submitted",
  incoming: "approved,sent,partial",
  draft: "draft",
  received: "received",
  closed: "closed",
  cancelled: "cancelled",
  all: undefined,
};

/** Pembelian > Purchase Order (Plan §6.18). */
export function PurchaseOrders() {
  const { t, formatDate, formatMoney, formatNumber } = useI18n();
  const { can } = useAuth();
  const [settings, setSettings] = useState(false);
  const navigate = useNavigate();
  const status = useStatusFilter(STATUS_CHOICES, "open");
  const search = useSearchFilter();
  const range = useDateRange();
  const page = useListPage();
  const [supplierId, setSupplierId] = useUrlFilter<string>("supplierId", "");

  const statusParam = STATUS_PARAM[status.value];
  const filters = { ...(statusParam ? { status: statusParam } : {}), ...(supplierId ? { supplierId } : {}), ...search.query, ...range.query };
  const list = useQuery({
    queryKey: ["purchase-orders", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/purchase-orders", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("po.title")}
        </Typography.Title>
        {can("procurement.manage") && (
          <Space>
            <Button icon={<SettingOutlined />} onClick={() => setSettings(true)}>
              {t("po.settings")}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/app/procurement/orders/new")}>
              {t("po.add")}
            </Button>
          </Space>
        )}
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <SearchFilter filter={search} placeholder={t("po.search")} />
        <StatusFilter
          filter={status}
          options={[
            { value: "open", label: t("po.filter.open") },
            { value: "waiting", label: t("po.filter.waiting") },
            { value: "incoming", label: t("po.filter.incoming") },
            { value: "draft", label: t("po.status.draft") },
            { value: "received", label: t("po.status.received") },
            { value: "closed", label: t("po.status.closed") },
            { value: "cancelled", label: t("po.status.cancelled") },
            { value: "all", label: t("list.allStatuses") },
          ]}
        />
        <SupplierSelect value={supplierId || undefined} onChange={(id) => setSupplierId(id ?? "")} allowClear style={{ width: 240 }} />
        <DateRangeFilter range={range} />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/purchase-orders/export"
          query={filters}
          filename={t("po.title")}
          columns={[
            { key: "number", label: t("po.number") },
            { key: "supplierName", label: t("po.supplier") },
            { key: "warehouseName", label: t("po.warehouse") },
            { key: "status", label: t("common.status"), values: Object.fromEntries(PO_STATUSES.map((v) => [v, t(`po.status.${v}`)])) },
            { key: "orderDate", label: t("po.orderDate"), kind: "date" },
            { key: "expectedDate", label: t("po.expectedDate"), kind: "date" },
            { key: "units", label: t("po.quantity"), kind: "number" },
            { key: "received", label: t("po.received"), kind: "number" },
            { key: "total", label: t("po.total"), kind: "number" },
            { key: "createdBy", label: t("po.createdBy") },
            { key: "approvedBy", label: t("po.approvedBy") },
            { key: "createdAt", label: t("list.createdAt"), kind: "datetime" },
            { key: "sentAt", label: t("po.status.sent"), kind: "datetime" },
          ]}
        />
      </Flex>
      <Table<PurchaseOrderRow>
        rowKey="id"
        loading={list.isFetching}
        dataSource={list.data?.items}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("po.empty") }}
        onRow={(o) => ({ onClick: () => navigate(`/app/procurement/orders/${o.id}`), style: { cursor: "pointer" } })}
        columns={[
          { title: t("po.number"), dataIndex: "number", width: 140, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          { title: t("po.supplier"), dataIndex: "supplierName" },
          { title: t("po.warehouse"), dataIndex: "warehouseName", width: 180 },
          { title: t("common.status"), dataIndex: "status", width: 180, render: (v: PurchaseOrderRow["status"]) => <PoStatusTag status={v} /> },
          {
            title: t("po.received"),
            width: 120,
            align: "right",
            render: (_, o) => (
              <span className="z-num">
                {formatNumber(o.received)} / {formatNumber(o.units)}
              </span>
            ),
          },
          { title: t("po.expectedDate"), dataIndex: "expectedDate", width: 130, render: (v: string | null) => (v ? formatDate(v) : "—") },
          { title: t("po.total"), dataIndex: "total", width: 150, align: "right", render: (v: number) => <span className="z-num">{formatMoney(v)}</span> },
        ]}
      />
      {settings && <ReceivingSettings onClose={() => setSettings(false)} />}
    </>
  );
}

type OverReceipt = "none" | "tolerance" | "any";

/** Receiving more than a purchase order asked for: never, within a tolerance, or any amount. */
function ReceivingSettings({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const current = useQuery({ queryKey: ["settings", "procurement"], queryFn: () => unwrap(api.GET("/v1/settings/procurement")) });
  const [mode, setMode] = useState<OverReceipt | null>(null);
  const [pct, setPct] = useState<number | null>(null);
  const m = mode ?? (current.data?.overReceipt as OverReceipt | undefined) ?? "any";
  const p = pct ?? (current.data?.overReceiptPct || 5);
  const save = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/settings/procurement", { body: { overReceipt: m, overReceiptPct: m === "tolerance" ? p : 0 } })),
    onSuccess: (res) => {
      queryClient.setQueryData(["settings", "procurement"], res);
      void message.success(t("po.settingsSaved"));
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal open title={t("po.settings")} okText={t("common.save")} cancelText={t("common.cancel")} confirmLoading={save.isPending}
      okButtonProps={{ disabled: !current.data }} onOk={() => save.mutate()} onCancel={onClose} destroyOnHidden>
      <Typography.Paragraph strong>{t("po.overReceipt")}</Typography.Paragraph>
      <Typography.Paragraph type="secondary">{t("po.overReceiptHelp")}</Typography.Paragraph>
      <Radio.Group value={m} onChange={(e) => setMode(e.target.value as OverReceipt)}>
        <Space orientation="vertical">
          <Radio value="none">{t("po.overReceipt.none")}</Radio>
          <Radio value="tolerance">
            <Space>
              {t("po.overReceipt.tolerance")}
              <InputNumber size="small" min={1} max={100} precision={0} suffix="%" value={p} disabled={m !== "tolerance"} onChange={(v) => setPct(v)} />
            </Space>
          </Radio>
          <Radio value="any">{t("po.overReceipt.any")}</Radio>
        </Space>
      </Radio.Group>
    </Modal>
  );
}
